import { Panel, PanelHeader } from './panel';
import { LockedOverlay } from './locked-overlay';
import {
  formatInteger,
  formatNumber,
  NO_VALUE
} from '@lib/page-utils/player-profile/format';

/** Rows a free visitor sees before the paywall. */
export const FREE_TEASER_ROW_COUNT = 3;

export type GameweekLogRow = {
  gameweek: number;
  opponentShortName: string;
  isHome: boolean;
  minutes: number | null;
  goals: number | null;
  assists: number | null;
  expectedGoals: number | null;
  expectedAssists: number | null;
  defensiveActions: number | null;
  points: number | null;
};

type GameweekLogProps = {
  rows: GameweekLogRow[];
  /** CBIT for defenders, CBIT+R for midfielders and forwards. */
  defensiveActionsLabel: string;
  isRestricted: boolean;
  onUnlockClick: () => void;
  /**
   * The season immediately before the selected one, and a way to switch to it.
   * Early in a new season the log is empty or nearly so, and the season control
   * lives in the filter bar at the top of the page, which by then has been
   * scrolled well out of sight. Omitted when there is no earlier season to
   * offer.
   */
  previousSeasonLabel?: string;
  onPreviousSeasonClick?: () => void;
};

const HEADER_CLASS =
  'px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-500';
const CELL_CLASS = 'px-3 py-3 text-sm tabular-nums';

/**
 * GW and Opponent identify the row, so they stay pinned while the stat columns
 * scroll under them. Both need an opaque background of their own: the scrolling
 * cells pass beneath, and a transparent sticky cell would show them through.
 * The offset for Opponent matches GW's rendered width, which is fixed by
 * `w-[52px]` rather than left to the table layout so the two cannot drift apart
 * and overlap.
 *
 * The edge between the pinned pair and the scrolling columns is drawn with an
 * inset shadow rather than a right border: under `border-collapse` a border on
 * a sticky cell is owned by the collapsed grid and scrolls away with it, while
 * a shadow is painted by the cell itself and stays put.
 */
const GAMEWEEK_COLUMN_WIDTH_PX = 52;
const STICKY_GAMEWEEK_CLASS = 'sticky left-0 z-20 w-[52px]';
const STICKY_OPPONENT_CLASS = 'sticky z-20 shadow-[inset_-1px_0_0_0_#EFEFEF]';

const LogRow = ({
  row,
  hasDefensiveActionsColumn
}: {
  row: GameweekLogRow;
  hasDefensiveActionsColumn: boolean;
}) => (
  <tr className="border-b border-neutral-100 last:border-b-0">
    <td
      className={`${CELL_CLASS} ${STICKY_GAMEWEEK_CLASS} bg-white font-bold text-neutral-800`}
    >
      {row.gameweek}
    </td>
    <td
      className={`${CELL_CLASS} ${STICKY_OPPONENT_CLASS} bg-white text-neutral-700`}
      style={{ left: GAMEWEEK_COLUMN_WIDTH_PX }}
    >
      {row.opponentShortName}
      <span className="text-neutral-400">{row.isHome ? ' (H)' : ' (A)'}</span>
    </td>
    <td className={`${CELL_CLASS} text-right text-neutral-800`}>
      {formatInteger(row.minutes)}
    </td>
    <td className={`${CELL_CLASS} text-right text-neutral-800`}>
      {formatInteger(row.goals)}
    </td>
    <td className={`${CELL_CLASS} text-right text-neutral-800`}>
      {formatInteger(row.assists)}
    </td>
    <td className={`${CELL_CLASS} text-right text-neutral-500`}>
      {formatNumber(row.expectedGoals, 2)}
    </td>
    <td className={`${CELL_CLASS} text-right text-neutral-500`}>
      {formatNumber(row.expectedAssists, 2)}
    </td>
    {hasDefensiveActionsColumn && (
      <td className={`${CELL_CLASS} text-right text-neutral-800`}>
        {formatInteger(row.defensiveActions)}
      </td>
    )}
    <td className={`${CELL_CLASS} text-right font-bold text-neutral-800`}>
      {row.points === null ? NO_VALUE : row.points}
    </td>
  </tr>
);

/**
 * Every appearance, with a free teaser of the most recent few rows and the rest
 * behind the paywall. Locked rows are never rendered — the restricted view is
 * given a truncated row set upstream, so no withheld value reaches the client.
 */
export const GameweekLog = ({
  rows,
  defensiveActionsLabel,
  isRestricted,
  onUnlockClick,
  previousSeasonLabel,
  onPreviousSeasonClick
}: GameweekLogProps) => {
  const hasDefensiveActionsColumn = defensiveActionsLabel.length > 0;
  const visibleRows = isRestricted
    ? rows.slice(0, FREE_TEASER_ROW_COUNT)
    : rows;

  return (
    <Panel className="overflow-hidden">
      <PanelHeader
        title="Gameweek log"
        subtitle="Every appearance with minutes, returns and defensive actions."
      />
      <div className="relative border-t border-neutral-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-100">
                <th
                  scope="col"
                  className={`${HEADER_CLASS} ${STICKY_GAMEWEEK_CLASS} bg-neutral-100 text-left`}
                >
                  GW
                </th>
                <th
                  scope="col"
                  className={`${HEADER_CLASS} ${STICKY_OPPONENT_CLASS} bg-neutral-100 text-left`}
                  style={{ left: GAMEWEEK_COLUMN_WIDTH_PX }}
                >
                  Opponent
                </th>
                <th scope="col" className={`${HEADER_CLASS} text-right`}>
                  Mins
                </th>
                <th scope="col" className={`${HEADER_CLASS} text-right`}>
                  G
                </th>
                <th scope="col" className={`${HEADER_CLASS} text-right`}>
                  A
                </th>
                <th scope="col" className={`${HEADER_CLASS} text-right`}>
                  xG
                </th>
                <th scope="col" className={`${HEADER_CLASS} text-right`}>
                  xA
                </th>
                {hasDefensiveActionsColumn && (
                  <th scope="col" className={`${HEADER_CLASS} text-right`}>
                    {defensiveActionsLabel}
                  </th>
                )}
                <th scope="col" className={`${HEADER_CLASS} text-right`}>
                  Points
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <LogRow
                  key={`${row.gameweek}-${row.opponentShortName}`}
                  row={row}
                  hasDefensiveActionsColumn={hasDefensiveActionsColumn}
                />
              ))}
            </tbody>
          </table>
        </div>

        {visibleRows.length === 0 && (
          <div className="px-5 py-8 text-center sm:px-6">
            <p className="text-sm text-neutral-500">
              No appearances recorded for these filters yet.
            </p>
            {previousSeasonLabel && onPreviousSeasonClick && (
              <button
                type="button"
                onClick={onPreviousSeasonClick}
                className="mt-3 rounded-md px-2 py-1 text-sm font-bold text-primary-600 underline underline-offset-2 hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              >
                See {previousSeasonLabel} game by game
              </button>
            )}
          </div>
        )}

        {isRestricted && rows.length > FREE_TEASER_ROW_COUNT && (
          <div className="relative border-t border-neutral-100 px-5 py-10 sm:px-6">
            <LockedOverlay
              title="Full gameweek history is a member feature"
              message="See every appearance this season, with expected goals, expected assists and defensive actions."
              onUnlockClick={onUnlockClick}
            />
          </div>
        )}
      </div>
    </Panel>
  );
};
